import { RipeningProgram } from "../../../../hooks/useRipeningPrograms";
import { dayjs } from "../../../../utils/date";

const DATE_URL_FORMAT = "YYYY-MM-DD";

export interface RipeningProgramSnapshot {
    id: string;
    name: string;
    fruit_type: string;
    duration_minutes: number;
    version: number;
    definition: RipeningProgram["definition"];
}

export function deriveProgramEndDate(startDate: string, durationMinutes: number): string | null {
    const start = dayjs(startDate);
    if (!start.isValid() || !Number.isFinite(durationMinutes) || durationMinutes < 1) return null;
    return start.add(durationMinutes, "minute").format(DATE_URL_FORMAT);
}

export function buildRipeningProgramSnapshot(program: RipeningProgram): RipeningProgramSnapshot {
    return {
        id: program.id,
        name: program.name,
        fruit_type: program.fruit_type,
        duration_minutes: program.duration_minutes,
        version: program.version,
        definition: JSON.parse(JSON.stringify(program.definition)),
    };
}

// * Cycle meta `ripening_program` used to be a plain option value (e.g. "1-times-gas").
// * Controller program cycles store a RipeningProgramSnapshot there instead, which the
// * location endpoint flattens into a JSON string. Resolve both to something displayable:
// * snapshot → its name (falling back to its id), anything else is returned unchanged.
export function getRipeningProgramDisplayValue<T>(value: T): T | string | null {
    let program: unknown = value;
    if (typeof value === "string") {
        if (!value.trim().startsWith("{")) return value;
        try {
            program = JSON.parse(value);
        } catch {
            return value;
        }
    }
    if (!program || typeof program !== "object" || Array.isArray(program)) return value;

    const { name, id } = program as Partial<RipeningProgramSnapshot>;
    if (typeof name === "string" && name.trim() !== "") return name;
    if (id !== null && id !== undefined && String(id) !== "") return String(id);
    return null;
}

export type CycleStartStatus = "pending" | "accepted" | "rejected" | "timeout";

export interface CycleStartCommand {
    command_id?: string;
    status?: CycleStartStatus;
    // * Plain-language reason of a failed start (set by the backend); never the raw detail.
    message?: string | null;
}

/** The program start command stored on a cycle (`meta.controller_command`), if any. */
export function getCycleStartCommand(cycle: any): CycleStartCommand | null {
    let command = cycle?.meta?.controller_command;
    if (typeof command === "string") {
        try {
            command = JSON.parse(command);
        } catch {
            return null;
        }
    }
    return command && typeof command === "object" && !Array.isArray(command) ? command : null;
}

export function isFailedStartStatus(status: string | null | undefined): status is "rejected" | "timeout" {
    return status === "rejected" || status === "timeout";
}

// * The room gives up on a start after 5 minutes; a start still "pending" well after that
// * could not be followed (e.g. stored before start states were tracked): show no state.
export const CYCLE_START_STALE_MS = 10 * 60 * 1000;

/** Cycle timestamps are stored as UTC even when the API omits an explicit offset. */
export function parseCycleStartTimestamp(value: string | number | null | undefined): number {
    if (value === null || value === undefined) return NaN;
    if (typeof value === "number") return dayjs(value).valueOf();
    const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
    return (hasOffset ? dayjs(value) : dayjs.utc(value)).valueOf();
}

/** "starting" while the room has not confirmed the start, "failed" after it did not start.
 *  `startedAt` (when the start was sent) lets an old, never resolved "pending" show nothing. */
export function getCycleStartState(
    status: string | null | undefined,
    startedAt?: string | number | null,
    now = Date.now(),
): "starting" | "failed" | null {
    if (status === "pending") {
        const sent = parseCycleStartTimestamp(startedAt);
        return Number.isFinite(sent) && now - sent > CYCLE_START_STALE_MS ? null : "starting";
    }
    if (isFailedStartStatus(status)) return "failed";
    return null;
}

// * Start override bounds, the same as the backend, the program service and the room enforce:
// * an end temperature -10..45 °C, a storage temperature -10..40 °C, a C2H4 dosage 0 or more.
const START_OVERRIDE_BOUNDS: Record<string, { min: number; max?: number; label: string; unit: string }> = {
    T_End: { min: -10, max: 45, label: "End temperature", unit: "°C" },
    Store_Setpoint: { min: -10, max: 40, label: "Storage temperature", unit: "°C" },
    Spv_C2H4: { min: 0, label: "C2H4 dosage", unit: "ppm" },
};

const overrideValueError = (key: string, value: unknown, where = ""): string | null => {
    const bounds = START_OVERRIDE_BOUNDS[key];
    if (!bounds) return null;
    const range = bounds.max === undefined ? `${bounds.min} ${bounds.unit} or more` : `between ${bounds.min} and ${bounds.max} ${bounds.unit}`;
    if (typeof value !== "number" || !Number.isFinite(value) || value < bounds.min || (bounds.max !== undefined && value > bounds.max)) {
        return `${where}${bounds.label} must be ${range}.`;
    }
    return null;
};

/** Problems with the program adjustments made at cycle start; empty when they can be sent. */
export function startOverrideErrors(override: any): string[] {
    if (!override || typeof override !== "object") return [];
    const errors: string[] = [];
    Object.entries(override.store ?? {}).forEach(([key, value]) => {
        const error = overrideValueError(key, value);
        if (error) errors.push(error);
    });
    (override.phases ?? []).forEach((phase: any) => {
        Object.entries(phase?.values ?? {}).forEach(([key, value]) => {
            const error = overrideValueError(key, value, `Phase ${phase.phase}: `);
            if (error) errors.push(error);
        });
    });
    return errors;
}
